import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink, Newspaper, Plus, Search, SearchX } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { useArticles } from '@/lib/queries';
import { liveUrl } from '@/lib/seo';
import type { Completeness, Locale } from '@/lib/types';
import { LOCALES } from '@/lib/types';
import { PageHeader } from '@/components/PageHeader';
import { Segmented } from '@/components/MediaPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { EmptyState, LiveDot, Skeleton } from '@/components/ui/Feedback';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://ipost.uz';
type Filter = 'all' | 'DRAFT' | 'PUBLISHED' | 'missing-ru' | 'missing-seo';

export function Articles() {
  const { t, lang, formatRelative } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const params: Record<string, unknown> = { q: query || undefined };
  if (filter === 'DRAFT' || filter === 'PUBLISHED') params.status = filter;
  if (filter === 'missing-ru') params.missing = 'ru';
  if (filter === 'missing-seo') params.missing = 'seo';

  const { data, isLoading, isPlaceholderData } = useArticles(params);
  const rows = data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t.articles.title}
        subtitle={t.articles.subtitle}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/articles/new')}>
            {t.articles.new}
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[14rem] max-w-sm flex-1">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.common.search}
              aria-label={t.common.search}
              className="pl-9"
            />
          </div>
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: t.articles.filterAll },
              { value: 'DRAFT', label: t.articles.filterDrafts },
              { value: 'PUBLISHED', label: t.articles.filterPublished },
              { value: 'missing-seo', label: t.articles.filterMissingSeo },
            ]}
          />
        </div>
      </PageHeader>

      <div className="mx-auto w-full max-w-content px-6 pb-12">
        {isLoading ? (
          <CardGridSkeleton />
        ) : rows.length === 0 ? (
          <div className="card">
            {query || filter !== 'all' ? (
              <EmptyState
                icon={<SearchX className="h-6 w-6" />}
                title={t.articles.noResultsTitle}
                hint={t.articles.noResultsHint}
                action={
                  <Button
                    variant="outline"
                    onClick={() => {
                      setQuery('');
                      setFilter('all');
                    }}
                  >
                    {t.articles.filterAll}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Newspaper className="h-6 w-6" />}
                title={t.articles.emptyTitle}
                hint={t.articles.emptyHint}
                action={
                  <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/articles/new')}>
                    {t.articles.emptyAction}
                  </Button>
                }
              />
            )}
          </div>
        ) : (
          <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3', isPlaceholderData && 'opacity-70')}>
            {rows.map((article) => {
              const title = article.title[lang] ?? article.title.uz ?? article.title.ru ?? t.articles.untitled;
              const hasTitle = Boolean(article.title[lang]);
              const excerpt = article.excerpt?.[lang] ?? article.excerpt?.uz ?? article.excerpt?.ru ?? null;
              const category = article.category?.name[lang] ?? article.category?.key ?? null;
              const cover = article.cover?.url ?? null;
              const live = article.status === 'PUBLISHED';

              return (
                <Link
                  key={article.id}
                  to={`/articles/${article.id}`}
                  className="card card-hover group flex flex-col overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                >
                  {/* Cover */}
                  <div className="relative aspect-[16/9] overflow-hidden bg-raised">
                    {cover ? (
                      <img
                        src={cover}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-quick ease-out group-hover:scale-[1.03]"
                      />
                    ) : (
                      <div className="grid h-full w-full place-items-center bg-gradient-to-br from-brand-500/10 via-raised to-accent-500/10">
                        <Newspaper aria-hidden className="h-9 w-9 text-faint/70" />
                      </div>
                    )}

                    {/* Status pill — legible over image or placeholder */}
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-chip bg-surface/85 px-2 py-0.5 text-eyebrow uppercase text-muted shadow-card backdrop-blur">
                      <LiveDot live={live} />
                      {live ? t.status.live : t.status.draft}
                    </span>

                    {/* Open on site — published only, reveals on hover */}
                    {live && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          window.open(liveUrl(SITE_URL, lang, 'news', article.slug), '_blank', 'noopener');
                        }}
                        aria-label={t.common.openOnSite}
                        className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-control bg-surface/85 text-muted opacity-0 shadow-card backdrop-blur transition-ui duration-instant hover:text-brand-500 focus-visible:opacity-100 group-hover:opacity-100"
                      >
                        <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-center justify-between gap-2">
                      {category ? (
                        <span className="max-w-[65%] truncate rounded-chip bg-brand-500/10 px-2 py-0.5 text-eyebrow uppercase text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                          {category}
                        </span>
                      ) : (
                        <span className="text-eyebrow uppercase text-faint">—</span>
                      )}
                      <span
                        className="shrink-0 text-ui text-faint"
                        title={article.updatedBy ? `${t.articles.columnUpdated}: ${article.updatedBy.name}` : undefined}
                      >
                        {formatRelative(article.updatedAt)}
                      </span>
                    </div>

                    <h3 className={cn('mt-2.5 line-clamp-2 text-[0.9375rem] font-semibold leading-snug', hasTitle ? 'text-strong' : 'text-muted')}>
                      {title}
                    </h3>

                    {excerpt ? (
                      <p className="mt-1.5 line-clamp-2 text-ui leading-relaxed text-muted">{excerpt}</p>
                    ) : (
                      <p className="mt-1.5 truncate font-mono text-code text-faint">/{article.slug}</p>
                    )}

                    <div className="mt-auto flex items-center justify-between gap-2 pt-4">
                      <LocaleChips completeness={article.completeness} />
                      <span className="inline-flex shrink-0 items-center gap-1 text-ui font-medium text-brand-600 transition-colors group-hover:text-brand-500 dark:text-brand-300">
                        {t.common.details}
                        <ArrowRight aria-hidden className="h-3.5 w-3.5 transition-transform duration-quick ease-out group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {data && data.total > 0 && (
          <p className="mt-4 text-ui text-faint">{t.articles.total(data.total)}</p>
        )}
      </div>
    </>
  );
}

/** A grid of placeholder cards while the list loads — matches the real shape. */
function CardGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="card overflow-hidden">
          <Skeleton className="aspect-[16/9] w-full rounded-none" />
          <div className="space-y-2.5 p-4">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The same three-slot vocabulary as the stamp rail, compressed to a chip row.
 * Solid = written, dashed = not — never red, because a missing translation is
 * a state, not a mistake.
 */
export function LocaleChips({ completeness }: { completeness: Record<Locale, Completeness> }) {
  return (
    <span className="flex items-center gap-1">
      {LOCALES.map((locale) => {
        const state = completeness[locale];
        return (
          <span
            key={locale}
            title={locale.toUpperCase()}
            className={cn(
              'inline-flex h-5 items-center rounded-chip border px-1.5 font-mono text-[0.625rem] uppercase leading-none',
              state === 'full' && 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-300',
              state === 'partial' && 'border-line bg-raised text-muted',
              state === 'empty' && 'border-dashed border-line/80 text-faint',
            )}
          >
            {locale}
          </span>
        );
      })}
    </span>
  );
}
